import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertCircle, CheckCircle, Loader2, ArrowLeft, Eye, GraduationCap, 
  Save, MapPin, ShieldCheck, Info, FileText, User, LayoutDashboard,
  Sparkles, ChevronRight
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formFillApi } from '../../services/formFillApi'; 
import FormDetailsInputs from './FormDetailsInputs';
import FormUploadAndReview from './FormUploadAndReview';

const StudentApplicationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { applicationId, viewOnly, draftData, fromCheckout, selectedColleges: initialSelectedColleges } = state;

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true); 
  const [saving, setSaving] = useState(false); 
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [draftId, setDraftId] = useState('');
  const [isViewMode, setIsViewMode] = useState(viewOnly || false);
  const [targetColleges, setTargetColleges] = useState([]);

  // ✅ IMMUTABLE STATE STRUCTURE with proper defaults
  const [formData, setFormData] = useState({
    collegeId: '',
    courseName: '',
    personalDetails: { 
      fullName: '', 
      fatherName: '', 
      motherName: '', 
      dateOfBirth: '', 
      gender: '', 
      nationality: 'Indian', 
      religion: '', 
      category: '', 
      aadharNumber: '', 
      maritalStatus: 'Single' 
    },
    contactDetails: { 
      email: '', 
      mobile: '', 
      permanentAddress: { 
        addressLine1: '', 
        addressLine2: '', 
        city: '', 
        state: '', 
        pinCode: '', 
        country: 'India' 
      }, 
      currentAddress: { 
        addressLine1: '', 
        addressLine2: '', 
        city: '', 
        state: '', 
        pinCode: '', 
        country: 'India' 
      }, 
      sameAsPermanent: false 
    },
    academicDetails: {
      class10: { 
        boardUniversity: '', 
        schoolCollege: '', 
        rollNumber: '', 
        passingYear: '', 
        percentage: '', 
        cgpa: '' 
      },
      class12: { 
        boardUniversity: '', 
        schoolCollege: '', 
        rollNumber: '', 
        passingYear: '', 
        percentage: '', 
        cgpa: '',
        stream: ''
      },
      graduation: { 
        boardUniversity: '', 
        schoolCollege: '', 
        rollNumber: '', 
        passingYear: '', 
        percentage: '', 
        cgpa: '' 
      }
    },
    documents: { 
      profilePhoto: '', 
      signature: '', 
      aadharCard: '',
      panCard: '',
      class10Marksheet: '',
      class10Certificate: '',
      class12Marksheet: '',
      class12Certificate: '',
      transferCertificate: '',
      migrationCertificate: '',
      incomeCertificate: '',
      casteCertificate: '',
      domicileCertificate: ''
    },
    applicationFee: 1000
  });

  // ✅ FIXED: Enhanced data normalization with better validation
  const getNormalizedPayload = useCallback(() => {
    const safeInt = (v) => {
      if (!v || v === '' || v === null || v === undefined) return null;
      const parsed = parseInt(v, 10);
      return isNaN(parsed) ? null : parsed;
    };
    
    const safeFloat = (v) => {
      if (!v || v === '' || v === null || v === undefined) return null;
      const parsed = parseFloat(v);
      return isNaN(parsed) ? null : parsed;
    };

    const safeString = (v) => {
      return v && typeof v === 'string' ? v.trim() : '';
    };

    // Create clean payload with only non-empty values
    const payload = {
      personalDetails: {
        fullName: safeString(formData.personalDetails.fullName),
        fatherName: safeString(formData.personalDetails.fatherName),
        motherName: safeString(formData.personalDetails.motherName),
        dateOfBirth: safeString(formData.personalDetails.dateOfBirth),
        gender: safeString(formData.personalDetails.gender),
        nationality: safeString(formData.personalDetails.nationality) || 'Indian',
        religion: safeString(formData.personalDetails.religion),
        category: safeString(formData.personalDetails.category),
        aadharNumber: safeString(formData.personalDetails.aadharNumber),
        maritalStatus: safeString(formData.personalDetails.maritalStatus) || 'Single'
        
      },
      contactDetails: {
        email: safeString(formData.contactDetails.email),
        mobile: safeString(formData.contactDetails.mobile),
        permanentAddress: {
          addressLine1: safeString(formData.contactDetails.permanentAddress?.addressLine1),
          addressLine2: safeString(formData.contactDetails.permanentAddress?.addressLine2),
          city: safeString(formData.contactDetails.permanentAddress?.city),
          state: safeString(formData.contactDetails.permanentAddress?.state),
          pinCode: safeString(formData.contactDetails.permanentAddress?.pinCode),
          country: safeString(formData.contactDetails.permanentAddress?.country) || 'India'
        },
        currentAddress: {
          addressLine1: safeString(formData.contactDetails.currentAddress?.addressLine1),
          addressLine2: safeString(formData.contactDetails.currentAddress?.addressLine2),
          city: safeString(formData.contactDetails.currentAddress?.city),
          state: safeString(formData.contactDetails.currentAddress?.state),
          pinCode: safeString(formData.contactDetails.currentAddress?.pinCode),
          country: safeString(formData.contactDetails.currentAddress?.country) || 'India'
        },
        sameAsPermanent: Boolean(formData.contactDetails.sameAsPermanent)
      },
      academicDetails: {
        class10: {
          boardUniversity: safeString(formData.academicDetails.class10?.boardUniversity),
          schoolCollege: safeString(formData.academicDetails.class10?.schoolCollege),
          rollNumber: safeString(formData.academicDetails.class10?.rollNumber),
          passingYear: safeInt(formData.academicDetails.class10?.passingYear),
          percentage: safeFloat(formData.academicDetails.class10?.percentage),
          cgpa: safeFloat(formData.academicDetails.class10?.cgpa)
        },
        class12: {
          boardUniversity: safeString(formData.academicDetails.class12?.boardUniversity),
          schoolCollege: safeString(formData.academicDetails.class12?.schoolCollege),
          rollNumber: safeString(formData.academicDetails.class12?.rollNumber),
          passingYear: safeInt(formData.academicDetails.class12?.passingYear),
          percentage: safeFloat(formData.academicDetails.class12?.percentage),
          cgpa: safeFloat(formData.academicDetails.class12?.cgpa),
          stream: safeString(formData.academicDetails.class12?.stream)
        },
        graduation: {
          boardUniversity: safeString(formData.academicDetails.graduation?.boardUniversity),
          schoolCollege: safeString(formData.academicDetails.graduation?.schoolCollege),
          rollNumber: safeString(formData.academicDetails.graduation?.rollNumber),
          passingYear: safeInt(formData.academicDetails.graduation?.passingYear),
          percentage: safeFloat(formData.academicDetails.graduation?.percentage),
          cgpa: safeFloat(formData.academicDetails.graduation?.cgpa)
        }
      },
      documents: {
        profilePhoto: safeString(formData.documents?.profilePhoto),
        signature: safeString(formData.documents?.signature),
        aadharCard: safeString(formData.documents?.aadharCard),
        panCard: safeString(formData.documents?.panCard),
        class10Marksheet: safeString(formData.documents?.class10Marksheet),
        class10Certificate: safeString(formData.documents?.class10Certificate),
        class12Marksheet: safeString(formData.documents?.class12Marksheet),
        class12Certificate: safeString(formData.documents?.class12Certificate),
        transferCertificate: safeString(formData.documents?.transferCertificate),
        migrationCertificate: safeString(formData.documents?.migrationCertificate),
        incomeCertificate: safeString(formData.documents?.incomeCertificate),
        casteCertificate: safeString(formData.documents?.casteCertificate),
        domicileCertificate: safeString(formData.documents?.domicileCertificate)
      },
      targetColleges: targetColleges || [],
      status: 'draft',
      applicationFee: formData.applicationFee || 1000
    };

    // Add draftId if exists
    if (draftId) {
      payload.draftId = draftId;
    }

    console.log('📤 Sending payload:', JSON.stringify(payload, null, 2));
    return payload;
  }, [formData, targetColleges, draftId]);

  // ✅ INITIALIZATION with better error handling
  const initializeFormData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      let finalData = null;
      let finalColleges = [];

      // Priority 1: Check LocalStorage/State for Colleges
      if (initialSelectedColleges?.length > 0) {
        finalColleges = initialSelectedColleges;
      } else {
        try {
          const local = localStorage.getItem('pendingCollegeSelection');
          if (local) {
            const parsed = JSON.parse(local);
            finalColleges = parsed.colleges || [];
          }
        } catch (e) {
          console.warn('Failed to parse localStorage:', e);
        }
      }

      // Priority 2: Fetch Data
      if (viewOnly && applicationId) {
        try {
          const res = await formFillApi.getApplicationById(applicationId);
          if (res.success) {
            finalData = res.application;
          }
        } catch (err) {
          console.error('Failed to fetch application:', err);
          setError('Failed to load application details');
        }
      } else {
        try {
          const res = await formFillApi.getMyApplications();
          const existingDraft = res.applications?.find(a => a.status === 'draft');
          if (existingDraft) {
            finalData = existingDraft;
            setDraftId(existingDraft.id || existingDraft._id);
            if (finalColleges.length === 0) {
              finalColleges = existingDraft.targetColleges || existingDraft.collegeDetails || [];
            }
          }
        } catch (err) {
          console.error('Failed to fetch applications:', err);
        }
      }

      // ✅ DEEP MERGE with proper null checks
      if (finalData) {
        setFormData(prev => ({
          ...prev,
          ...finalData,
          personalDetails: { 
            ...prev.personalDetails, 
            ...(finalData.personalDetails || {}) 
          },
          contactDetails: { 
            ...prev.contactDetails, 
            ...(finalData.contactDetails || {}),
            permanentAddress: { 
              ...prev.contactDetails.permanentAddress, 
              ...(finalData.contactDetails?.permanentAddress || {}) 
            },
            currentAddress: { 
              ...prev.contactDetails.currentAddress, 
              ...(finalData.contactDetails?.currentAddress || {}) 
            }
          },
          academicDetails: {
            ...prev.academicDetails,
            class10: { ...prev.academicDetails.class10, ...(finalData.academicDetails?.class10 || {}) },
            class12: { ...prev.academicDetails.class12, ...(finalData.academicDetails?.class12 || {}) },
            graduation: { ...prev.academicDetails.graduation, ...(finalData.academicDetails?.graduation || {}) }
          },
          documents: {
            ...prev.documents,
            ...(finalData.documents || {})
          }
        }));
      }

      if (finalColleges.length > 0) {
        setTargetColleges(finalColleges);
      } else if (!viewOnly) {
        navigate('/allcollege');
      }
    } catch (err) {
      console.error('Initialization error:', err);
      setError("Failed to initialize form. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [applicationId, viewOnly, initialSelectedColleges, navigate]);

  useEffect(() => {
    initializeFormData();
  }, [initializeFormData]);

  // ✅ FIXED: Better save draft with error logging
  const handleSaveDraft = async (silent = false) => {
    if (!silent) setSaving(true);
    setError('');
    
    try {
      const payload = getNormalizedPayload();
      
      console.log('💾 Attempting to save draft...');
      const response = await formFillApi.saveDraft(payload);
      
      console.log('✅ Save response:', response);
      
      if (response.success) {
        const savedId = response.id || response.draftId || response.data?.id;
        if (savedId) {
          setDraftId(savedId);
        }
        
        if (!silent) {
          setSuccess('✅ Progress saved successfully!');
          setTimeout(() => setSuccess(''), 3000);
        }
        
        return savedId;
      } else {
        throw new Error(response.message || 'Failed to save draft');
      }
    } catch (err) {
      console.error('❌ Save draft error:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      const errorMessage = err.response?.data?.message 
        || err.message 
        || 'Failed to save. Please check all fields and try again.';
      
      setError(errorMessage);
      
      if (!silent) {
        setTimeout(() => setError(''), 5000);
      }
      
      return null;
    } finally {
      if (!silent) setSaving(false);
    }
  };

  const handleProceedToPayment = async () => {
    setLoading(true);
    const savedId = await handleSaveDraft(true);
    
    if (savedId || draftId) {
      navigate('/student/checkout', { 
        state: { 
          applicationData: formData, 
          draftId: savedId || draftId, 
          selectedColleges: targetColleges 
        } 
      });
    } else {
      setError('Please save your progress before proceeding to payment');
      setLoading(false);
    }
  };

  // Update form data handler
  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], ...data }
    }));
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full blur-2xl opacity-20 animate-pulse"></div>
        <Loader2 className="relative animate-spin w-16 h-16 text-indigo-600 mb-6"/>
      </div>
      <p className="text-slate-600 font-bold tracking-[0.3em] text-xs uppercase mt-4">Loading Application</p>
      <div className="flex gap-2 mt-6">
        <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
        <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/20 pt-20 pb-32 px-4 md:px-8 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-pink-100/20 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Modern Header with gradient */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12 gap-6">
          <div className="flex items-start gap-6">
            <button 
              onClick={() => navigate('/student/dashboard')} 
              className="group p-4 bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-indigo-100 hover:border-indigo-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <LayoutDashboard size={24} className="text-slate-700 group-hover:text-indigo-600 transition-colors"/>
            </button>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent tracking-tight leading-tight">
                  Application Form
                </h1>
                <Sparkles className="text-indigo-400 w-8 h-8 animate-pulse" />
              </div>
              <p className="text-slate-500 text-sm font-semibold tracking-wide">Academic Year 2025-26 • Admission Process</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {!isViewMode && (
              <button 
                onClick={() => handleSaveDraft(false)} 
                disabled={saving}
                className="group relative bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-bold text-sm tracking-wide flex items-center gap-3 hover:shadow-2xl hover:shadow-indigo-300/50 transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative flex items-center gap-3">
                  {saving ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Save size={18}/>
                  )}
                  <span>{saving ? 'Saving...' : 'Save Progress'}</span>
                </div>
              </button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Main Form Area */}
          <div className="lg:col-span-8 space-y-6">
            {/* Alert Messages */}
            {error && (
              <div className="group p-5 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-3xl flex items-start gap-4 font-semibold text-sm shadow-2xl shadow-red-300/50 animate-shake border-2 border-red-300">
                <AlertCircle size={22} className="shrink-0 mt-0.5"/>
                <div className="flex-1">
                  <p className="font-bold mb-1">Error</p>
                  <p className="opacity-90">{error}</p>
                </div>
              </div>
            )}
            
            {success && (
              <div className="group p-5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-3xl flex items-start gap-4 font-semibold text-sm shadow-2xl shadow-emerald-300/50 animate-slideDown border-2 border-emerald-300">
                <CheckCircle size={22} className="shrink-0 mt-0.5"/>
                <div className="flex-1">
                  <p className="font-bold mb-1">Success</p>
                  <p className="opacity-90">{success}</p>
                </div>
              </div>
            )}

            {/* Form Card */}
            <div className="group bg-white/90 backdrop-blur-xl rounded-[2rem] shadow-2xl border border-indigo-100/50 p-8 md:p-12 overflow-hidden relative hover:shadow-indigo-200/50 transition-all duration-500">
              {/* Decorative gradient overlay */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-indigo-100/50 to-transparent rounded-full blur-3xl -z-10"></div>
              
              {/* Render appropriate component based on step */}
              {currentStep <= 3 ? (
                <FormDetailsInputs 
                  currentStep={currentStep} 
                  formData={formData} 
                  updateFormData={updateFormData} 
                  readOnly={isViewMode} 
                />
              ) : (
                <FormUploadAndReview
                  formData={formData}
                  updateFormData={updateFormData}
                  readOnly={isViewMode}
                  targetColleges={targetColleges}
                />
              )}
              
              {/* Navigation Buttons */}
              <div className="mt-16 flex flex-col sm:flex-row justify-between gap-4 border-t border-indigo-100 pt-10">
                {currentStep > 1 && (
                  <button 
                    onClick={() => {
                      setCurrentStep(c => c - 1); 
                      window.scrollTo({top: 0, behavior: 'smooth'});
                    }}
                    className="group px-10 py-4 bg-slate-100 text-slate-700 rounded-2xl font-bold text-sm tracking-wide hover:bg-slate-200 transition-all duration-300 flex items-center justify-center gap-2 hover:-translate-x-1"
                  >
                    <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform"/>
                    <span>Previous</span>
                  </button>
                )}
                
                <button 
                  onClick={currentStep === 4 ? handleProceedToPayment : () => {
                    setCurrentStep(c => c + 1); 
                    window.scrollTo({top: 0, behavior: 'smooth'});
                  }}
                  className="group relative sm:ml-auto px-12 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl font-bold text-sm tracking-wide shadow-2xl shadow-indigo-300/50 hover:shadow-purple-300/50 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center gap-2">
                    <span>{currentStep === 4 ? 'Proceed to Payment' : 'Continue'}</span>
                    <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform"/>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Stunning Sidebar */}
          <div className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              {/* College Selection Card */}
              <div className="group bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 rounded-[2rem] p-8 text-white shadow-2xl shadow-indigo-900/50 border border-indigo-500/20 overflow-hidden relative hover:shadow-purple-900/50 transition-all duration-500">
                {/* Animated background glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="p-4 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-3xl shadow-xl shadow-indigo-500/30">
                      <GraduationCap size={32} className="text-white"/>
                    </div>
                    <div>
                      <h3 className="font-black text-xl tracking-tight">Your Selection</h3>
                      <p className="text-indigo-300 text-sm font-semibold mt-1">{targetColleges.length} Institution{targetColleges.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  <div className="space-y-3 max-h-[450px] overflow-y-auto pr-2 custom-scrollbar">
                    {targetColleges.map((col, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center gap-4 p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl group/item hover:bg-white/10 hover:border-indigo-400/30 transition-all duration-300 hover:translate-x-1"
                      >
                        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center font-black text-sm shrink-0 shadow-lg">
                          {idx + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-bold text-sm truncate text-white group-hover/item:text-indigo-200 transition-colors">
                            {col.collegeName || col.name}
                          </h4>
                          <p className="text-xs text-indigo-300 font-medium mt-1 flex items-center gap-1.5">
                            <MapPin size={12}/>
                            <span>{col.city || 'Location'}</span>
                          </p>
                        </div>
                      </div>
                    ))}
                    
                    {targetColleges.length === 0 && (
                      <div className="text-center py-12 text-indigo-300/50">
                        <GraduationCap size={48} className="mx-auto mb-4 opacity-30"/>
                        <p className="text-sm font-semibold">No colleges selected</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/10 text-center">
                    <button 
                      onClick={() => navigate('/allcollege', { state: { selectedColleges: targetColleges }})}
                      className="group/btn text-sm font-bold text-indigo-300 hover:text-white transition-all duration-300 flex items-center gap-2 mx-auto hover:gap-3"
                    >
                      <span className="tracking-wide">Modify Selection</span>
                      <ChevronRight size={16} className="group-hover/btn:translate-x-1 transition-transform"/>
                    </button>
                  </div>
                </div>
              </div>

              {/* Help Card */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-[2rem] p-6 border border-indigo-200/50 shadow-lg">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-indigo-100 rounded-xl">
                    <Info size={20} className="text-indigo-600"/>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Need Help?</h4>
                    <p className="text-xs text-slate-600 mt-1">Your progress is auto-saved</p>
                  </div>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Fill out all required fields carefully. You can save your progress at any time and return later to complete your application.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        @keyframes slideDown {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(99, 102, 241, 0.5);
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(99, 102, 241, 0.7);
        }
      `}</style>
    </div>
  );
};

export default StudentApplicationForm;